import React, { useState } from 'react';
import {
    Form,
    Input,
    Button,
    InputNumber,
    Select,
    Typography,
    Space,
    Alert,
    Divider,
    Tag,
    Switch,
    Checkbox,
    Collapse,
    message
} from 'antd';
import DatePicker from '../../utils/momentDatePicker';
import {
    RocketOutlined,
    GlobalOutlined,
    ClockCircleOutlined,
    LockOutlined,
    StarOutlined,
    PlayCircleOutlined,
    TeamOutlined,
    SettingOutlined
} from '@ant-design/icons';
import AuctionTypeAlert from './AuctionTypeAlert';
import BiddingTypeAlert from './BiddingTypeAlert';
import { videoUrlValidator } from '../../utils/videoUtils';
import VideoPreviewModal from '../VideoPreviewModal';
import LocationAutocomplete from './LocationAutocomplete';
import env from '../../env';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AuctionForm = ({
    form,
    loading,
    isEdit,
    categories,
    isPrivate,
    setIsPrivate,
    biddingType,
    setBiddingType,
    onImageModalClick,
    onSubmit,
    disabled = false,
    agents = [],
    hasAgents = false,
    setHasAgents
}) => {
    const [videoModalVisible, setVideoModalVisible] = useState(false);
    const videoUrl = Form.useWatch('video_url', form);
    const hasAgentsValue = Form.useWatch('has_agents', form);
    const isFeatured = Form.useWatch('is_featured', form);

    // Default terms and conditions text for new auctions
    const defaultTermsConditions = `General Terms & Conditions

By placing an order with us, you agree to our policies and terms of service. All product details, pricing, and availability are subject to change without notice. We reserve the right to refuse or cancel any order at our discretion.

Payment Terms

All prices are listed in PKR.

Payment must be made in full at the time of purchase.

Accepted payment methods: bank transfer, credit/debit card, or any method shown at checkout.

Order Confirmation: An order is considered confirmed once payment has been successfully processed.

Prices may change without prior notice.`;

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={{
                is_featured: false,
                has_agents: false,
                agent_ids: [],
                terms_conditions: !isEdit ? defaultTermsConditions : undefined,
                lot_close_time_seconds: !isEdit ? 60 : undefined,
                is_private: !isEdit ? false : undefined,
                is_sequential: !isEdit ? false : undefined
            }}
            className="auction-form"
        >
            {!isEdit && (
                <Alert
                    message="Your auction will be created as 'Scheduled'"
                    description="Contact admin to complete payment so it becomes visible to bidders."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24, borderRadius: 8 }}
                />
            )}

            {/* Essential Details */}
            <Title level={4} style={{ marginBottom: 20 }}>Auction Details</Title>

            <Form.Item
                name="title"
                label="Auction Title"
                rules={[{ required: true, message: 'A title is required' }]}
            >
                <Input
                    placeholder="e.g., Premium Livestock Auction - Karachi, December 2024"
                    maxLength={100}
                    showCount
                    disabled={disabled}
                    size="large"
                    style={{ borderRadius: '8px' }}
                />
            </Form.Item>

            <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please provide a description' }]}
            >
                <TextArea
                    rows={4}
                    placeholder="Describe your auction: what items are available, and anything bidders should know."
                    maxLength={1000}
                    showCount
                    disabled={disabled}
                    size="large"
                    style={{ borderRadius: '8px' }}
                />
            </Form.Item>

            <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
            >
                <Select
                    placeholder="Search and select a category..."
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%', borderRadius: '8px' }}
                    disabled={disabled}
                    size="large"
                >
                    {categories.map(category => (
                        <Select.Option key={category.id} value={category.id} label={category.name}>
                            <Space size="small">
                                <span style={{ fontSize: '16px' }} dangerouslySetInnerHTML={{ __html: category.icon }} />
                                <Text>{category.name}</Text>
                            </Space>
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="start_time"
                label="Start Time"
                rules={[{ required: true, message: 'Please select when your auction will start' }]}
            >
                <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: '100%', borderRadius: '8px' }}
                    placeholder="Select start date & time"
                    disabled={disabled}
                    size="large"
                />
            </Form.Item>

            <Form.Item name="location" label="Location (Optional)">
                <LocationAutocomplete
                    placeholder="e.g., Karachi, Lahore, Islamabad"
                    disabled={disabled}
                    size="large"
                    style={{ borderRadius: '8px' }}
                />
            </Form.Item>

            <Divider style={{ margin: '24px 0' }} />

            <Collapse
                bordered={false}
                style={{ background: 'transparent' }}
                items={[
                    {
                        key: 'advanced',
                        forceRender: true,
                        label: (
                            <Space>
                                <SettingOutlined />
                                <span style={{ fontWeight: 600, fontSize: 15 }}>Advanced Settings (optional)</span>
                            </Space>
                        ),
                        children: (
                            <div>
                                {/* Auction Type & Bidding */}
                                <Form.Item
                                    name="is_private"
                                    label="Auction Type"
                                    rules={[{ required: true, message: 'Please select whether your auction is public or private' }]}
                                >
                                    <Select
                                        onChange={(value) => setIsPrivate(value)}
                                        style={{ width: '100%', borderRadius: '8px' }}
                                        optionLabelProp="label"
                                        disabled={disabled}
                                        size="large"
                                    >
                                        <Select.Option value={false} label="Public Auction">
                                            <Space><GlobalOutlined style={{ color: '#1890ff' }} /> Public Auction</Space>
                                        </Select.Option>
                                        <Select.Option value={true} label="Private Auction">
                                            <Space><LockOutlined style={{ color: '#fa8c16' }} /> Private Auction (invitation only)</Space>
                                        </Select.Option>
                                    </Select>
                                </Form.Item>

                                {isPrivate !== null && (
                                    <AuctionTypeAlert isPrivate={isPrivate} onImageModalClick={onImageModalClick} />
                                )}

                                <Form.Item
                                    name="is_sequential"
                                    label="Bidding Type"
                                    rules={[{ required: true, message: 'Please select a bidding type' }]}
                                    style={{ marginTop: 16 }}
                                >
                                    <Select
                                        onChange={(value) => setBiddingType(value)}
                                        style={{ width: '100%', borderRadius: '8px' }}
                                        optionLabelProp="label"
                                        disabled={disabled}
                                        size="large"
                                    >
                                        <Select.Option value={false} label="Simultaneous Bidding">
                                            <Space><RocketOutlined style={{ color: '#52c41a' }} /> Simultaneous (multiple items at once)</Space>
                                        </Select.Option>
                                        <Select.Option value={true} label="Sequential Bidding">
                                            <Space><ClockCircleOutlined style={{ color: '#f5222d' }} /> Sequential (one item at a time)</Space>
                                        </Select.Option>
                                    </Select>
                                </Form.Item>

                                {biddingType !== null && (
                                    <BiddingTypeAlert biddingType={biddingType} />
                                )}

                                <Divider style={{ margin: '24px 0' }} />

                                {/* Lot Timing */}
                                <Form.Item
                                    name="lot_close_time_seconds"
                                    label="Lot Close Time (seconds)"
                                    rules={[
                                        { required: true, message: 'Please set the lot close time' },
                                        { type: 'number', min: 60, max: 300, message: 'Must be between 60 and 300 seconds' }
                                    ]}
                                    extra={
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            How long a lot stays open after the last bid. Min 60s, max 300s.
                                        </Text>
                                    }
                                >
                                    <InputNumber
                                        precision={0}
                                        onBlur={() => {
                                            const value = form.getFieldValue('lot_close_time_seconds');
                                            const num = parseInt(value, 10);
                                            if (!isNaN(num)) {
                                                if (num > 300) {
                                                    form.setFieldValue('lot_close_time_seconds', 300);
                                                    message.warning('Lot close time cannot exceed 300 seconds. Set to 300.');
                                                } else if (num < 60) {
                                                    form.setFieldValue('lot_close_time_seconds', 60);
                                                }
                                            }
                                        }}
                                        style={{ width: '100%', borderRadius: '8px' }}
                                        placeholder="Enter seconds (default: 60)"
                                        disabled={disabled}
                                        addonAfter="seconds"
                                        size="large"
                                    />
                                </Form.Item>

                                <Divider style={{ margin: '24px 0' }} />

                                {/* Video */}
                                <Form.Item
                                    name="video_url"
                                    label="Promotional Video URL"
                                    rules={[{ validator: videoUrlValidator }]}
                                    extra={
                                        <Space direction="vertical" size={8}>
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                Paste a YouTube, Vimeo, or TikTok link to showcase your auction.
                                            </Text>
                                            {videoUrl && (
                                                <Button
                                                    type="dashed"
                                                    icon={<PlayCircleOutlined />}
                                                    onClick={() => setVideoModalVisible(true)}
                                                >
                                                    Preview Video
                                                </Button>
                                            )}
                                        </Space>
                                    }
                                >
                                    <Input
                                        placeholder="https://youtube.com/watch?v=..."
                                        prefix={<PlayCircleOutlined style={{ color: '#1890ff' }} />}
                                        disabled={disabled}
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>

                                <VideoPreviewModal
                                    visible={videoModalVisible}
                                    onClose={() => setVideoModalVisible(false)}
                                    videoUrl={videoUrl}
                                    title="Auction Video Preview"
                                    width={900}
                                    height={500}
                                />

                                <Divider style={{ margin: '24px 0' }} />

                                {/* Agents */}
                                <Form.Item name="has_agents" valuePropName="checked" style={{ marginBottom: hasAgentsValue ? 16 : 0 }}>
                                    <Checkbox
                                        disabled={disabled}
                                        onChange={(e) => {
                                            if (setHasAgents) setHasAgents(e.target.checked);
                                            if (!e.target.checked) form.setFieldsValue({ agent_ids: [] });
                                        }}
                                    >
                                        <Space><TeamOutlined /> Assign agents to this auction</Space>
                                    </Checkbox>
                                </Form.Item>

                                {hasAgentsValue && (
                                    agents.length === 0 ? (
                                        <Alert
                                            message="No Agents Available"
                                            description={
                                                <Button type="link" onClick={() => window.open('/agents', '_blank')} style={{ padding: 0 }}>
                                                    Go to Agents Management
                                                </Button>
                                            }
                                            type="warning"
                                            showIcon
                                        />
                                    ) : (
                                        <Form.Item
                                            name="agent_ids"
                                            label="Select Agents"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (hasAgentsValue && (!value || value.length === 0)) {
                                                            return Promise.reject(new Error('Please select at least one agent'));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select agents to assign..."
                                                style={{ width: '100%' }}
                                                showSearch
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                disabled={disabled}
                                                maxTagCount="responsive"
                                                optionLabelProp="label"
                                                tagRender={({ label, closable, onClose }) => (
                                                    <Tag color="blue" closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                                                        {label}
                                                    </Tag>
                                                )}
                                            >
                                                {agents.map(agent => (
                                                    <Select.Option key={agent.id} value={agent.id} label={agent.agent_name}>
                                                        <Space size="small">
                                                            {agent.photo_url ? (
                                                                <img
                                                                    src={agent.photo_url.startsWith('http') ? agent.photo_url : env.BACKEND_URL + agent.photo_url}
                                                                    alt={agent.agent_name}
                                                                    style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div style={{
                                                                    width: 24, height: 24, borderRadius: '50%',
                                                                    background: '#1890ff', color: 'white',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12
                                                                }}>
                                                                    {agent.agent_name.charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                            <Text>{agent.agent_name}</Text>
                                                        </Space>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )
                                )}

                                <Divider style={{ margin: '24px 0' }} />

                                {/* Terms & Conditions */}
                                <Form.Item name="terms_conditions" label="Terms & Conditions">
                                    <TextArea
                                        rows={6}
                                        placeholder="Payment terms, delivery conditions, return policy, etc."
                                        maxLength={2000}
                                        showCount
                                        disabled={disabled}
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>

                                {!isEdit && (
                                    <>
                                        <Divider style={{ margin: '24px 0' }} />
                                        <Form.Item
                                            name="is_featured"
                                            label={<Space><StarOutlined style={{ color: isFeatured ? '#faad14' : '#8c8c8c' }} /> Featured Auction</Space>}
                                            valuePropName="checked"
                                            extra={
                                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                                    {isFeatured
                                                        ? 'Featured auctions get extra visibility. Additional charges apply — contact admin.'
                                                        : 'Get extra visibility on the homepage and category pages (additional charges apply).'}
                                                </Text>
                                            }
                                        >
                                            <Switch checkedChildren="Featured" unCheckedChildren="Regular" disabled={disabled} />
                                        </Form.Item>
                                    </>
                                )}
                            </div>
                        )
                    }
                ]}
            />
        </Form>
    );
};

export default AuctionForm;
